import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI, attendeesAPI, publicCustomFormsAPI } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import LivenessFaceCapture from '../components/LivenessFaceCapture';
import PhoneInput from 'react-phone-input-2';
import { useToast } from '../context/ToastContext';
import IframeFormRenderer from '../components/IframeFormRenderer';
import 'react-phone-input-2/lib/style.css';

const PublicForm = () => {
  const { slug } = useParams();
  const toast = useToast();
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [step, setStep] = useState(1); // 1: form, 2: facial, 3: photo, 4: success
  const [attendeeId, setAttendeeId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  
  // Custom form state
  const [customForm, setCustomForm] = useState(null);
  const [useCustomForm, setUseCustomForm] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  // Helper function to construct proper image URLs
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it already has the correct path, return as is
    if (imagePath.startsWith('/uploads/branding/')) {
      return imagePath;
    }
    // Just the filename, need to prepend the path
    return `/uploads/branding/${imagePath}`;
  };

  useEffect(() => {
    loadProject();
    return () => {
      if (stream) stream.getTracks().forEach(track => track.stop());
    };
  }, [slug]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.getBySlug(slug);
      setProject(response.data.project);
      
      // Aplicar branding si existe
      if (response.data.project.branding) {
        const branding = response.data.project.branding;
        document.documentElement.style.setProperty('--primary-color', branding.primary_color || '#3B82F6');
        document.documentElement.style.setProperty('--secondary-color', branding.secondary_color || '#10B981');
      }
      
      // Fetch custom form configuration
      try {
        const customFormResponse = await publicCustomFormsAPI.getCustomForm(slug);
        console.log('📋 Custom form response:', customFormResponse.data);
        
        // La respuesta tiene estructura: { success: true, data: { ... } }
        // El formulario está en customFormResponse.data.data
        const response = customFormResponse.data;
        const rawData = response.data; // Acceder al objeto anidado
        
        if (rawData && rawData.is_active && rawData.html_content) {
          // Convertir de snake_case a camelCase para consistencia con el frontend
          const cf = {
            id: rawData.id,
            projectId: rawData.project_id,
            htmlContent: rawData.html_content,
            cssContent: rawData.css_content,
            jsContent: rawData.js_content,
            isActive: rawData.is_active,
            createdAt: rawData.created_at,
            updatedAt: rawData.updated_at
          };
          setCustomForm(cf);
          setUseCustomForm(true);
          console.log('✅ Custom form found and active for project:', slug, cf);
        } else {
          console.log('⚠️ Custom form not active or empty:', {
            hasData: !!rawData,
            isActive: rawData?.is_active,
            hasHtml: !!rawData?.html_content
          });
          setUseCustomForm(false);
        }
      } catch (customFormError) {
        // No custom form exists for this project - use default
        console.log('❌ No custom form found, using default form:', customFormError.message);
        setUseCustomForm(false);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Proyecto no encontrado');
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData({...formData, [fieldName]: value});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar aceptación de términos
    if (!acceptedPrivacy) {
      toast.warning('Debe aceptar los Términos y Condiciones y la Política de Privacidad para continuar.');
      return;
    }

    // Prevenir múltiples envíos
    if (loading) {
      console.log('⚠️ Ya hay un registro en proceso');
      return;
    }

    setLoading(true);

    try {
      const response = await attendeesAPI.register(slug, formData);
      setAttendeeId(response.data.attendeeId);
      setQrCode(response.data.qrCode);

      // Verificar si hay campos de facial o photo que requieren captura
      const hasFacialField = project.fields.some(f => f.fieldType === 'facial_recognition' && formData[f.fieldName]);
      const hasPhotoField = project.fields.some(f => f.fieldType === 'photo' && formData[f.fieldName]);

      if (hasFacialField) {
        setStep(2);
        startCamera();
      } else if (hasPhotoField) {
        setStep(3);
        startCamera();
      } else {
        setStep(4);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.error || 'Error en el registro. Por favor intenta nuevamente.');
      setLoading(false); // Solo resetear loading en caso de error
    }
    // No resetear loading aquí - se resetea cuando se completa el flujo completo
  };

  // Handle form submission from iframe (custom form)
  const handleIframeFormSubmit = async (iframeFormData) => {
    console.log('📝 Received form data from iframe:', iframeFormData);
    
    // Validate required legal acceptance
    if (!iframeFormData.accepted_terms && !iframeFormData.acceptedPrivacy) {
      toast.warning('Debe aceptar los Términos y Condiciones y la Política de Privacidad para continuar.');
      return;
    }

    // Prevenir múltiples envíos
    if (loading) {
      console.log('⚠️ Ya hay un registro en proceso');
      return;
    }

    setLoading(true);

    try {
      // Extract facial consent if present
      const hasFacialConsent = iframeFormData.facial_consent === true || iframeFormData.facial_consent === 'true';
      
      // Prepare form data for API (remove internal fields)
      const submissionData = { ...iframeFormData };
      delete submissionData.accepted_terms;
      delete submissionData.facial_consent;
      
      const response = await attendeesAPI.register(slug, submissionData);
      setAttendeeId(response.data.attendeeId);
      setQrCode(response.data.qrCode);
      setFormData(submissionData);

      // If facial consent was given, go to facial capture step
      if (hasFacialConsent) {
        setStep(2);
        startCamera();
      } else {
        setStep(4);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.error || 'Error en el registro. Por favor intenta nuevamente.');
      setLoading(false);
    }
  };

  // Handle form data changes from iframe
  const handleIframeFormDataChange = (iframeFormData) => {
    console.log('📝 Form data changed in iframe:', iframeFormData);
    // Update local formData to keep track
    setFormData(prev => ({ ...prev, ...iframeFormData }));
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 1280, height: 720 } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('No se pudo acceder a la cámara. Asegúrate de dar permisos.');
    }
  };

  // Manejar captura facial con liveness detection
  const handleLivenessCaptureComplete = async (frames) => {
    setLoading(true);
    try {
      console.log(`📸 Captura liveness completada con ${frames.length} frames`);

      // CRÍTICO: Triple sanitización para evitar BindingError
      // Convertir frames a JSON y luego parsear para eliminar CUALQUIER referencia
      const cleanFrames = JSON.parse(JSON.stringify(frames.map(frame => ({
        angle: String(frame.angle),
        image: String(frame.image),
        timestamp: Number(frame.timestamp) || Date.now(),
        pose: {
          yaw: Number(frame.pose?.yaw) || 0,
          pitch: Number(frame.pose?.pitch) || 0,
          roll: Number(frame.pose?.roll) || 0
        }
      }))));

      console.log(`🧹 Frames sanitizados: ${cleanFrames.length}, tamaño aprox: ${Math.round(JSON.stringify(cleanFrames).length / 1024)}KB`);

      // Enviar frames al backend para procesamiento con ONNX
      await attendeesAPI.uploadFacialPhotoLiveness(attendeeId, cleanFrames);

      // Verificar si también necesita foto normal
      const hasPhotoField = project.fields.some(f => f.fieldType === 'photo' && formData[f.fieldName]);
      if (hasPhotoField) {
        setStep(3);
      } else {
        setStep(4);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.error || 'Error al procesar el reconocimiento facial.';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleLivenessCaptureError = (error) => {
    console.error('Error en captura liveness:', error);
    toast.error('Error al inicializar la cámara. Por favor verifica los permisos.');
    setLoading(false);
  };

  // Captura de foto simple (para step 3 - photo)
  const capturePhoto = async () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);

    canvas.toBlob(async (blob) => {
      setLoading(true);
      try {
        const fileName = `photo-${attendeeId}-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        await attendeesAPI.uploadPhoto(attendeeId, file);
        if (stream) stream.getTracks().forEach(track => track.stop());
        setStep(4);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al procesar la foto. Por favor intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    }, 'image/jpeg', 0.95);
  };

  const renderField = (field) => {
    const commonProps = {
      className: "input-field",
      required: field.isRequired,
      onChange: (e) => handleInputChange(field.fieldName, e.target.value)
    };

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'number':
      case 'date':
        return <input type={field.fieldType} {...commonProps} />;

      case 'tel':
        return (
          <PhoneInput
            country={'pe'}
            value={formData[field.fieldName] || ''}
            onChange={(phone) => handleInputChange(field.fieldName, phone)}
            inputClass="input-field"
            containerClass="phone-input-container"
            buttonClass="phone-input-button"
            dropdownClass="phone-input-dropdown"
            searchClass="phone-input-search"
            enableSearch={true}
            disableSearchIcon={false}
            placeholder="Ingresa tu número de teléfono"
            required={field.isRequired}
            inputProps={{
              required: field.isRequired,
              name: field.fieldName
            }}
          />
        );

      case 'textarea':
        return <textarea {...commonProps} rows="4" />;

      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Selecciona una opción</option>
            {field.options && JSON.parse(field.options).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              className="mr-2"
              required={field.isRequired}
              onChange={(e) => handleInputChange(field.fieldName, e.target.checked)}
            />
            <span>{field.fieldLabel}</span>
          </div>
        );

      case 'facial_recognition':
        return (
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded min-w-0">
              <input
                type="checkbox"
                id={`facial-${field.id}`}
                className="mr-2 w-5 h-5 flex-shrink-0"
                onChange={(e) => handleInputChange(field.fieldName, e.target.checked)}
              />
              <label htmlFor={`facial-${field.id}`} className="cursor-pointer">
                <svg className="w-5 h-5 mr-2 text-blue-600 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>Activar reconocimiento facial para acceso rápido</span>
              </label>
            </div>
            {formData[field.fieldName] && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <p className="text-yellow-800">
                  <strong>Consentimiento expreso para datos biométricos:</strong> Al activar esta opción, 
                  usted autoriza la captura de su imagen facial, el procesamiento para obtener su descriptor 
                  biométrico y su uso para verificación de identidad durante el evento. 
                  Estos datos serán eliminados al finalizar el evento. 
                  Puede ejercer sus derechos ARCO consultando nuestra{' '}
                  <Link to="/privacidad" target="_blank" className="text-blue-600 hover:underline">Política de Privacidad</Link>.
                </p>
              </div>
            )}
          </div>
        );

      case 'photo':
        return (
          <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded">
            <input
              type="checkbox"
              id={`photo-${field.id}`}
              className="mr-2"
              onChange={(e) => handleInputChange(field.fieldName, e.target.checked)}
            />
            <label htmlFor={`photo-${field.id}`} className="flex items-center cursor-pointer">
              <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Tomar foto para el mosaico del evento</span>
            </label>
          </div>
        );

      default:
        return <input type="text" {...commonProps} />;
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-700">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  const primaryColor = project.branding?.primary_color || '#3B82F6';
  const backgroundColor = project.branding?.background_color || '#FFFFFF';

  return (
    <div 
      className="min-h-screen py-12 px-4"
      style={{background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`}}
    >
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header del proyecto */}
        {project.branding?.header_image && (
          <div className="w-full h-32 overflow-hidden">
            <img
              src={getImageUrl(project.branding.header_image)}
              alt="Header"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-8">
          {/* Step 0: Aviso de Privacidad */}
          {step === 0 && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-center mb-4" style={{color: primaryColor}}>
                Aviso de Privacidad
              </h1>
              
              <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto text-sm text-gray-700">
                <p className="mb-3"><strong>Responsable:</strong> {project?.name || 'Organización del evento'}</p>
                <p className="mb-3"><strong>Finalidad:</strong> Sus datos serán utilizados exclusivamente para:</p>
                <ul className="list-disc pl-5 mb-3">
                  <li>Registro en el evento</li>
                  <li>Generación de código QR de acceso</li>
                  <li>Control de ingreso al evento</li>
                </ul>
                
                <p className="mb-3"><strong>Datos biométricos:</strong> El reconocimiento facial es opcional. Si lo activa, su descriptor facial será almacenado de forma segura durante el evento y eliminado posteriormente.</p>
                
                <p className="mb-3"><strong>Conservación:</strong> Sus datos serán eliminados después de finalizar el evento.</p>
                
                <p className="mb-3"><strong>Derechos:</strong> Puede ejercer sus derechos de acceso, rectificación, cancelación y oposición contactando al administrador.</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mt-1 mr-3 w-5 h-5"
                  />
                  <span className="text-sm text-gray-700">
                    He leído y acepto el{' '}
                    <Link to="/terminos" target="_blank" className="text-blue-600 hover:underline">Términos y Condiciones</Link>
                    {' '}y la{' '}
                    <Link to="/privacidad" target="_blank" className="text-blue-600 hover:underline">Política de Privacidad</Link>.
                    Soy mayor de edad y proporciono mis datos de forma voluntaria.
                  </span>
                </label>
              </div>

              <button 
                type="button"
                onClick={() => acceptedPrivacy && setStep(1)}
                disabled={!acceptedPrivacy}
                className="w-full py-3 px-4 rounded-lg text-white font-semibold shadow-lg transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{backgroundColor: primaryColor}}
              >
                Continuar al Registro
              </button>

              <p className="text-center text-xs text-gray-500">
                Al continuar, usted acepta los términos descritos anteriormente.
              </p>
            </div>
          )}

          {step === 1 && (
            <>
              <h1 className="text-3xl font-bold text-center mb-2" style={{color: primaryColor}}>
                {project.name}
              </h1>
              {project.description && (
                <p className="text-center text-gray-600 mb-8">{project.description}</p>
              )}

              {/* Custom Form - Renders instead of default form when active */}
          {useCustomForm && customForm && (
            <IframeFormRenderer
              html={customForm.htmlContent}
              css={customForm.cssContent}
              js={customForm.jsContent}
              fields={project.fields}
              termsUrl={`/terminos/${slug}`}
              privacyUrl={`/privacidad/${slug}`}
              onFormSubmit={handleIframeFormSubmit}
              onFormDataChange={handleIframeFormDataChange}
              projectSlug={slug}
              project={project}
            />
          )}

          {/* Default Form - Rendered only when no custom form */}
          {!useCustomForm && (
          <form onSubmit={handleSubmit} className="space-y-6">
                {project.fields
                  .filter(f => f.fieldType !== 'facial_recognition' && f.fieldType !== 'photo')
                  .map(field => (
                    <div key={field.id}>
                      {field.fieldType !== 'checkbox' && (
                        <label className="label">
                          {field.fieldLabel} 
                          {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                      )}
                      {renderField(field)}
                    </div>
                  ))}

                {/* Campos especiales: facial y photo */}
                <div className="space-y-3">
                  {project.fields
                    .filter(f => f.fieldType === 'facial_recognition' || f.fieldType === 'photo')
                    .map(field => (
                      <div key={field.id}>
                        {renderField(field)}
                      </div>
                    ))}
                </div>

                {/* Aviso de privacidad - mismo estilo que facial */}
                <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded min-w-0">
                  <input
                    type="checkbox"
                    id="acceptedPrivacy"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mr-2 w-5 h-5 flex-shrink-0"
                  />
                  <label htmlFor="acceptedPrivacy" className="cursor-pointer text-sm text-gray-700">
                    He leído y acepto los{' '}
                    <Link to="/terminos" target="_blank" className="text-blue-600 hover:underline">Términos y Condiciones</Link>
                    {' '}y la{' '}
                    <Link to="/privacidad" target="_blank" className="text-blue-600 hover:underline">Política de Privacidad</Link>.
                    Soy mayor de edad y proporciono mis datos de forma voluntaria.
                  </label>
                </div>

                <button 
                  type="submit" 
                  disabled={loading || !acceptedPrivacy}
                  className="w-full py-3 px-4 rounded-lg text-white font-semibold shadow-lg transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{backgroundColor: primaryColor}}
                >
                  {loading ? 'Procesando...' : 'Registrarse al Evento'}
                </button>
              </form>
              )}
            </>
          )}

          {/* Step 2: Liveness Detection con MediaPipe */}
          {step === 2 && (
            <div className="text-center px-4">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full animate-pulse"
                     style={{
                       background: `linear-gradient(135deg, ${primaryColor}20 0%, ${primaryColor}40 100%)`,
                       boxShadow: `0 4px 20px ${primaryColor}30`
                     }}>
                  <svg className="w-10 h-10" style={{color: primaryColor}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-4xl font-extrabold mb-3 tracking-tight" style={{color: primaryColor}}>
                  Verificación de Identidad
                </h2>
                <p className="text-gray-600 text-lg max-w-md mx-auto">
                  Sigue las instrucciones para capturar tu rostro desde diferentes ángulos
                </p>
              </div>

              <LivenessFaceCapture
                onComplete={handleLivenessCaptureComplete}
                onError={handleLivenessCaptureError}
              />

              {project.fields.every(f => !f.isRequired || f.fieldType !== 'facial_recognition') && (
                <div className="mt-6">
                  <button
                    onClick={() => setStep(4)}
                    disabled={loading}
                    className="py-3 px-6 rounded-xl bg-gray-400 text-white font-semibold shadow-lg transition-all hover:bg-gray-500 disabled:opacity-50"
                  >
                    Saltar este paso
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Foto simple */}
          {step === 3 && (
            <div className="text-center px-4">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full animate-pulse"
                     style={{
                       background: `linear-gradient(135deg, ${primaryColor}20 0%, ${primaryColor}40 100%)`,
                       boxShadow: `0 4px 20px ${primaryColor}30`
                     }}>
                  <svg className="w-10 h-10" style={{color: primaryColor}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-4xl font-extrabold mb-3 tracking-tight" style={{color: primaryColor}}>
                  Toma tu foto
                </h2>
                <p className="text-gray-600 text-lg max-w-md mx-auto">
                  Sonríe y captura tu mejor foto para aparecer en el mosaico del evento
                </p>
              </div>

              {/* Container del video */}
              <div className="relative mb-8 mx-auto" style={{maxWidth: '550px'}}>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl"
                     style={{boxShadow: `0 20px 60px ${primaryColor}30`}}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full"
                    style={{
                      minHeight: '450px',
                      maxHeight: '600px',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)' // Efecto espejo
                    }}
                  />

                  {/* Estado de carga overlay */}
                  {loading && (
                    <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-20">
                      <div className="text-center">
                        <div className="inline-block w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mb-4"
                             style={{borderColor: primaryColor, borderTopColor: 'transparent'}}></div>
                        <p className="text-white text-lg font-semibold">Procesando...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-4 justify-center flex-wrap">
                <button
                  onClick={() => capturePhoto()}
                  disabled={loading}
                  className="group relative flex items-center gap-3 py-5 px-10 rounded-2xl text-white font-bold text-lg shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:scale-100 disabled:cursor-not-allowed"
                  style={{
                    background: loading ? '#9CA3AF' : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                    boxShadow: loading ? 'none' : `0 10px 40px ${primaryColor}40`
                  }}
                >
                  {loading ? (
                    <>
                      <div className="w-6 h-6 border-3 border-t-transparent border-white rounded-full animate-spin"></div>
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-7 h-7 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                        <circle cx="12" cy="13" r="3" strokeWidth={2.5}/>
                      </svg>
                      <span>Capturar Foto</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 4 && qrCode && (
            <div className="text-center">
              <div className="mb-6">
                <svg className="w-20 h-20 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h2 className="text-3xl font-bold text-green-600 mb-2">
                ¡Registro Exitoso!
              </h2>
              <p className="text-gray-600 mb-6">
                Tu código QR ha sido generado. Guárdalo para ingresar al evento.
              </p>

              <div className="flex justify-center mb-6 p-6 bg-gray-50 rounded-lg">
                <QRCodeSVG value={qrCode} size={256} level="H" />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  💡 <strong>Importante:</strong> Haz una captura de pantalla o guarda este código QR. 
                  También lo recibirás por email si proporcionaste tu correo.
                </p>
              </div>

              <button
                onClick={() => window.location.reload()}
                className="py-2 px-6 rounded-lg text-white font-semibold shadow-lg transition-all duration-200 hover:shadow-xl"
                style={{backgroundColor: primaryColor}}
              >
                Registrar otro asistente
              </button>
            </div>
          )}
        </div>

        {/* Footer del proyecto */}
        {project.branding?.footer_image && (
          <div className="w-full h-24 overflow-hidden">
            <img
              src={getImageUrl(project.branding.footer_image)}
              alt="Footer"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicForm;
